import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Col,
  Flex,
  List,
  Row,
  Skeleton,
  Space,
  Switch,
  Tooltip,
} from "antd";
import { useCallback, useMemo, useState } from "react";
import { createUseStyles } from "react-jss";
import useNotificationsApi from "../apiHooks/useNotificationsApi";
import { timeAgo } from "../common/utils/common";
import { _STYLES } from "../common/utils/customStyles";
import NotificationsComponent from "./notifications.component";
import notificationsStyles from "./notificationsStyles";

const { subTextHeader, subText, filterHeader, w100, colorSubText, textSm2 } =
  _STYLES;

const { notificationBody, notiHeader } = notificationsStyles;
const useStyles = createUseStyles({
  subTextHeader,
  subText,
  filterHeader,
  w100,
  notificationBody,
  notiHeader,
  colorSubText,
  textSm2,
});

const Notifications = () => {
  const classes = useStyles();

  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  const {
    getAllNotificationsApi: {
      data: notificationsData,
      get: fetchNotifications,
      isLoading,
    },
    markNotificationAsReadApi: { patch: markNotificationAsRead },
  } = useNotificationsApi();

  const handleNotification = useCallback(
    async (id) => {
      const res = await markNotificationAsRead(id);
      if (res) {
        fetchNotifications();
      }
    },
    [fetchNotifications, markNotificationAsRead]
  );

  const filteredNotificationData = useMemo(() => {
    return showOnlyUnread
      ? notificationsData.filter((item) => !item.isRead)
      : notificationsData;
  }, [notificationsData, showOnlyUnread]);

  const renderHeader = useMemo(
    () => (
      <Row
        className={classes.notiHeader}
        gutter={[24, 0]}
        align={"top"}
        justify={"space-between"}
      >
        <Col>
          <h3 className={classes.filterHeader}>Notifications</h3>
        </Col>
        <Col>
          <Space align="center">
            <span className={classes.subText}>Only show unread</span>
            <Switch
              size="small"
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              value={showOnlyUnread}
              onChange={setShowOnlyUnread}
            />
          </Space>
        </Col>
      </Row>
    ),
    [classes.filterHeader, classes.notiHeader, classes.subText, showOnlyUnread]
  );

  const renderBody = useMemo(
    () => (
      <List
        className={classes.notificationBody}
        loading={isLoading}
        itemLayout="horizontal"
        dataSource={filteredNotificationData}
        renderItem={(item) => (
          <List.Item>
            <Skeleton title={false} loading={item.loading} active>
              <Flex
                className={classes.w100}
                align={"middle"}
                gutter={[12, 0]}
                justify={"space-between"}
              >
                <Space size={0} direction="vertical" className={classes.w100}>
                  <h5 className={classes.subTextHeader}>{item.body}</h5>
                  <p className={classes.subText + " " + classes.colorSubText}>
                    {timeAgo(item.dateTime)}
                  </p>
                </Space>
                {!item.isRead && (
                  <Tooltip
                    mouseEnterDelay={1}
                    title={<p className={classes.textSm2}>Mark as read</p>}
                  >
                    <Button
                      size="small"
                      shape="circle"
                      type="text"
                      onClick={() => handleNotification(item.id)}
                    >
                      <Badge status="processing" />
                    </Button>
                  </Tooltip>
                )}
              </Flex>
            </Skeleton>
          </List.Item>
        )}
      />
    ),
    [
      classes.colorSubText,
      classes.notificationBody,
      classes.subText,
      classes.subTextHeader,
      classes.textSm2,
      classes.w100,
      filteredNotificationData,
      handleNotification,
      isLoading,
    ]
  );

  const unreadNotificationsCount = useMemo(
    () =>
      showOnlyUnread
        ? filteredNotificationData.length
        : notificationsData.filter((item) => !item.isRead).length,
    [filteredNotificationData.length, notificationsData, showOnlyUnread]
  );

  return (
    <NotificationsComponent
      renderHeader={renderHeader}
      renderBody={renderBody}
      count={unreadNotificationsCount}
    />
  );
};

export default Notifications;
